import { generatePassphrase, type SyncManager } from '@wiztime/sync';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useServices } from '../../hooks/useServices';
import { toastError, toastInfo } from '../../shell/toastStore';
import { Button, ButtonVariant } from '../../ui/Button';
import { RecoverySheet } from './RecoverySheet';
import styles from './Sync.module.css';
import { syncFailureKey } from './syncErrors';
import { toConfigUrl } from './urls';

interface GenerateFlowProps {
  readonly manager: SyncManager;
  readonly serverUrl: string;
  readonly onBack: () => void;
}

/** Shows a new 12-word passphrase; sync starts only after the user confirms they saved it. */
export function GenerateFlow({ manager, serverUrl, onBack }: GenerateFlowProps) {
  const { t, i18n } = useTranslation();
  const { sync } = useServices();
  const [phrase] = useState(() => generatePassphrase());
  const words = phrase.split(' ');
  const [spaceIdPrefix, setSpaceIdPrefix] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [printing, setPrinting] = useState(false);

  // Derive the keys now: the recovery sheet needs the space identifier, and enabling reuses the result.
  useEffect(() => {
    let alive = true;
    sync.previewSpaceId(phrase).then(
      (prefix) => {
        if (alive) setSpaceIdPrefix(prefix);
      },
      () => undefined,
    );
    return () => {
      alive = false;
      sync.discardPreview();
    };
  }, [sync, phrase]);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(phrase);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toastError(t('sync.generate.copyFailed'));
    }
  };

  const enable = async (): Promise<void> => {
    setEnabling(true);
    setFailure(null);
    try {
      await manager.enableSync({ serverUrl: toConfigUrl(serverUrl), passphrase: phrase });
      toastInfo(t('sync.generate.enabled'));
    } catch (error) {
      console.warn('[sync] enabling failed', error);
      setFailure(t(syncFailureKey(error)));
      setEnabling(false);
    }
  };

  return (
    <div className={styles.flow} data-testid="sync-generate">
      <h3 className={styles.flowTitle}>{t('sync.generate.title')}</h3>
      <p className={styles.muted}>{t('sync.generate.intro')}</p>
      <ol className={styles.words} data-testid="passphrase-words">
        {words.map((word, index) => (
          <li key={index} data-testid="passphrase-word">
            <span className={styles.wordNumber}>{index + 1}</span>
            <span>{word}</span>
          </li>
        ))}
      </ol>
      <p className={styles.spaceLine} data-testid="passphrase-space">
        {spaceIdPrefix === null
          ? t('sync.generate.spaceLoading')
          : t('sync.generate.space', { prefix: spaceIdPrefix })}
      </p>
      <div className={styles.actions}>
        <Button onClick={() => void copy()} data-testid="passphrase-copy">
          {copied ? t('sync.generate.copied') : t('sync.generate.copy')}
        </Button>
        <Button onClick={() => setPrinting(true)} data-testid="passphrase-print">
          {t('sync.generate.print')}
        </Button>
      </div>
      <p className={styles.warning}>{t('sync.generate.warning')}</p>
      <label className={styles.checkRow}>
        <input
          type="checkbox"
          checked={saved}
          disabled={enabling}
          onChange={(event) => setSaved(event.target.checked)}
          data-testid="passphrase-saved"
        />
        <span>{t('sync.generate.saved')}</span>
      </label>
      {enabling ? <DeriveProgress /> : null}
      {failure === null ? null : (
        <p className={styles.errorText} role="alert" data-testid="sync-failure">
          {failure}
        </p>
      )}
      <div className={styles.actions}>
        <Button disabled={enabling} onClick={onBack} data-testid="sync-back">
          {t('sync.back')}
        </Button>
        <Button
          variant={ButtonVariant.Primary}
          disabled={!saved || enabling}
          onClick={() => void enable()}
          data-testid="sync-enable"
        >
          {t('sync.generate.enable')}
        </Button>
      </div>
      {printing ? (
        <RecoverySheet
          words={words}
          spaceIdPrefix={spaceIdPrefix}
          dateText={new Date().toLocaleDateString(i18n.language)}
          onDone={() => setPrinting(false)}
        />
      ) : null}
    </div>
  );
}

/** Indeterminate progress while the passphrase is turned into keys (Argon2, about 1 to 3 seconds). */
export function DeriveProgress() {
  const { t } = useTranslation();
  return (
    <div className={styles.progress} role="status" data-testid="sync-progress">
      <progress aria-label={t('sync.progress')} />
      <span>{t('sync.progress')}</span>
    </div>
  );
}
